type EncryptionDefinition =
  | boolean
  | {
      [querySignature: string]: EncryptionDefinition;
    };
type Entities = "Auction" | "AuctionRequest" | "Event" | "Patient" | "Chat";

const SINGULAR_ENCRYPTED_PROPERTY_NAME_PATIENT = "patient" as const;
const SINGULAR_ENCRYPTED_PROPERTY_NAME_AUCTION = "auction" as const;
const SINGULAR_ENCRYPTED_PROPERTY_NAME_AUCTION_REQUEST =
  "auctionRequest" as const;
const SINGULAR_ENCRYPTED_PROPERTY_NAME_EVENT = "event" as const;
const SINGULAR_ENCRYPTED_PROPERTY_NAME_CHAT = "chat" as const;

const _SINGULAR_ENCRYPTED_PROPERTY_NAMES = [
  SINGULAR_ENCRYPTED_PROPERTY_NAME_PATIENT,
  SINGULAR_ENCRYPTED_PROPERTY_NAME_AUCTION,
  SINGULAR_ENCRYPTED_PROPERTY_NAME_AUCTION_REQUEST,
  SINGULAR_ENCRYPTED_PROPERTY_NAME_EVENT,
  SINGULAR_ENCRYPTED_PROPERTY_NAME_CHAT,
] as const;

type Singular = (typeof _SINGULAR_ENCRYPTED_PROPERTY_NAMES)[number];

const PLURAL_ENCRYPTED_PROPERTY_NAME_PATIENT = "patients" as const;
const PLURAL_ENCRYPTED_PROPERTY_NAME_AUCTION = "auctions" as const;
const PLURAL_ENCRYPTED_PROPERTY_NAME_AUCTION_REQUEST = "requests" as const;
const PLURAL_ENCRYPTED_PROPERTY_NAME_EVENT = "events" as const;
const PLURAL_ENCRYPTED_PROPERTY_NAME_CHAT = "chats" as const;

const _PLURAL_ENCRYPTED_PROPERTY_NAMES = [
  PLURAL_ENCRYPTED_PROPERTY_NAME_PATIENT,
  PLURAL_ENCRYPTED_PROPERTY_NAME_AUCTION,
  PLURAL_ENCRYPTED_PROPERTY_NAME_AUCTION_REQUEST,
  PLURAL_ENCRYPTED_PROPERTY_NAME_EVENT,
  PLURAL_ENCRYPTED_PROPERTY_NAME_CHAT,
] as const;

type Plural = (typeof _PLURAL_ENCRYPTED_PROPERTY_NAMES)[number];

/**
 * 1) CREATE ENTITIES
 */

const AuctionFields = {
  admissions: {
    profile: {
      care_frequency_other: true,
      current_care_facility_other: true,
      insurance_other: true,
      main_contact_first_name: true,
      main_contact_last_name: true,
      main_contact_phone: true,
      preexisting_conditions: true,
      referral_sources_other: true,
      relationship_other: true,
      required_care_other: true,
      search_reason_other: true,
      special_care_needs_other: true,
      visit_times_other: true,
    },
  },
  forms: {
    form_data: true,
  },
  medical_supplies: {
    delivery_address: {
      encrypted_house_number: true,
    },
  },
  profile: {
    search_destination: {
      encrypted_house_number: true,
    },
    search_location: {
      encrypted_house_number: true,
    },
  },
  rehab_forms: {
    general_form: { form_data: true },
    medical_form: { form_data: true },
  },
  session_key_context: true,
  transitional_care: {
    form_data: true,
  },
};

const Patient: EncryptionDefinition = {
  auction: AuctionFields,
  auctions: AuctionFields,
  profile: {
    birth_date: true,
    communication: {
      guardian_contact_information: true,
      guardians: {
        city: true,
        first_name: true,
        house_number: true,
        last_name: true,
        phone: true,
        street: true,
        type: true,
        zipcode: true,
      },
      patient_is_contact_description: true,
      relatives: {
        city: true,
        first_name: true,
        house_number: true,
        last_name: true,
        phone: true,
        relationship: true,
        relationship_other: true,
        street: true,
        zipcode: true,
      },
      relatives_description: true,
    },
    financing: {
      insurance_number: true,
    },
    first_name: true,
    general_practitioner: {
      city: true,
      first_name: true,
      honorific_prefix: true,
      honorific_suffix: true,
      house_number: true,
      job_title: true,
      last_name: true,
      phone: true,
      street: true,
      zipcode: true,
    },
    height: true,
    last_name: true,
    psychiatrist: {
      city: true,
      first_name: true,
      honorific_prefix: true,
      honorific_suffix: true,
      house_number: true,
      job_title: true,
      last_name: true,
      phone: true,
      street: true,
      zipcode: true,
    },
    weight: true,
  },
  session_key_context: true,
  transport: {
    accompanying_person: true,
  },
};

const Auction: EncryptionDefinition = {
  ...AuctionFields,
  patient: Patient,
};

const AuctionRequest: EncryptionDefinition = {
  auction: Auction,
  careprovider_notes: {
    message: true,
  },
  session_key_context: true,
};

const Event: EncryptionDefinition = {
  auction: Auction,
};

const Chat: EncryptionDefinition = {
  messages: true,
  session_key_context: true,
  title: true,
};

/**
 * 2) LIST ENTITIES
 */
// TODO: unit test single session_key_context per entity
const Entities: { [key in Entities]: EncryptionDefinition } = {
  Auction,
  AuctionRequest,
  Chat,
  Event,
  Patient,
};

// note that encrypted entities have to use these property names otherwise the patcher wont find them
// for instance, if we had auction.auction_request and wanted to encrypt/decrypt the auction_request as an Entity AuctionRequest,
// we would have to change the "Singular" value for the entity AuctionRequest from auctionRequest to auction_request for all cases
// including those in the in the apollo signiture:
// query getAuctionRequest($id: Int!, $ref: String) {
// here ==> auctionRequest(id: $id, ref: $ref)

export const Typenames: { [key in Entities]: [Singular, Plural] } = {
  Auction: [
    SINGULAR_ENCRYPTED_PROPERTY_NAME_AUCTION,
    PLURAL_ENCRYPTED_PROPERTY_NAME_AUCTION,
  ],
  AuctionRequest: [
    SINGULAR_ENCRYPTED_PROPERTY_NAME_AUCTION_REQUEST,
    PLURAL_ENCRYPTED_PROPERTY_NAME_AUCTION_REQUEST,
  ],
  Chat: [
    SINGULAR_ENCRYPTED_PROPERTY_NAME_CHAT,
    PLURAL_ENCRYPTED_PROPERTY_NAME_CHAT,
  ],
  Event: [
    SINGULAR_ENCRYPTED_PROPERTY_NAME_EVENT,
    PLURAL_ENCRYPTED_PROPERTY_NAME_EVENT,
  ],
  Patient: [
    SINGULAR_ENCRYPTED_PROPERTY_NAME_PATIENT,
    PLURAL_ENCRYPTED_PROPERTY_NAME_PATIENT,
  ],
};

/**
 * 3) CREATE MODEL DEFINITION
 */
// for an apollo signiture that looks like this:
// ...
// mutation updateEntity($id: Int! $input: Any!) {
//   entity(id: $id, input: $input)
// ...
//
// the pattern for the model definition should follow:
//   updateEntity: {
//      entity: EncryptionDefinition
//   }
//
// if the encrypted value is nested in the fragment, you should reflect that nesting in the model definition
//
//  i.e. if your input looks like this:
//    operationName: "updateEntity",
//    variables: { input: { context: { entity: TO_ENCRYPT } } }
//
// the pattern for the encryptPatcherModelDefinition should follow:
//  updateEntity: {
//     entity: {
//      context: {
//        entity: EncryptionDefinition
//      }
//     }
//  }
//
//  if your response is in a different structure, you can reflect that structure in the decryptPatcherModelDefinition:

// for operations that the encryption layer should encrypt add the operation here
export const encryptPatcherModelDefinition = {
  createAuction: {
    auction: Entities.Auction,
  },
  createChat: {
    chat: Entities.Chat,
  },
  updateAdmissionsResources: {
    auction: Entities.Auction,
  },
  updateDoctorRehabForms: {
    auction: AuctionFields, // input does not have a patient
  },
  usePatientAction: {
    patient: {
      context: {
        patient: Entities.Patient,
      },
    },
  },
  useSearchAction: {
    auction: {
      context: {
        auction: Entities.Auction,
      },
    },
  },
};

// for operations that the decryption layer should decrypt add the operation here
export const decryptPatcherModelDefinition = {
  createAdditionalAuction: {
    auction: Entities.Auction,
  },
  createAuction: encryptPatcherModelDefinition.createAuction,
  getAuction: {
    auction: Entities.Auction,
  },
  getAuctionForms: {
    auction: Entities.Auction,
  },
  getAuctionRequest: {
    auctionRequest: Entities.AuctionRequest,
  },
  getAuctionWithWinnerContacts: {
    auction: Entities.Auction,
  },
  getCareProviderAdmissionsAuction: {
    auction: Entities.Auction,
  },
  getCareproviderNotifications: {
    careproviderNotifications: { events: Entities.Event },
  },
  getCareproviderRequestsNew: {
    careproviderRequestsNew: { requests: Entities.AuctionRequest },
  },
  getChat: {
    chat: Entities.Chat,
  },
  getChats: {
    chatsPage: { chats: Entities.Chat },
  },
  getDoctorRehabForms: {
    auction: AuctionFields,
  },
  getPatient: {
    patient: Entities.Patient,
  },
  getPatientsNew: {
    patientsNew: {
      patients: Entities.Patient,
    },
  },
  getRehabForms: {
    auction: AuctionFields,
  },
  getTransitionalCare: {
    auction: Entities.Auction,
  },
  updateChat: {
    chat: Entities.Chat,
  },
  updateDoctorRehabForms: encryptPatcherModelDefinition.updateDoctorRehabForms,
  usePatientAction: {
    patient: Entities.Patient,
  },
  useRequestAction: {
    auctionRequest: Entities.AuctionRequest,
  },
  useSearchAction: {
    auction: Entities.Auction,
  },
};
